import Box from '@mui/material/Box';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type {
  WazuhFimEndpointItem,
  WazuhFimEventItem,
  WazuhFimEventsQuery,
  WazuhFimRule,
  WazuhFimSummaryData,
} from 'src/types/mindtime/wazuhFileMonitoring';
import type { Column } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrKpiCard,
  EdrSeverityPill,
  EdrStatusPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  formatEdrDateTime,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
  type EdrStatusTone,
} from '../shared/edrPrimitives';

import { toEdrSeverityLevel, EDR_SEVERITY_FILTER_OPTIONS } from '../shared/edrFormat';

export const fimSeverityToLevel = toEdrSeverityLevel;
export const FIM_SEVERITY_FILTER_OPTIONS = EDR_SEVERITY_FILTER_OPTIONS;

export const CHANGE_TYPE_COLORS: Record<string, string> = {
  modified: EDR_COLORS.medium,
  deleted: EDR_COLORS.critical,
  added: EDR_COLORS.brand500,
  renamed: EDR_COLORS.slate500,
};

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toFimTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};


export const fimStatusToTone = (status: string | undefined): EdrStatusTone => {
  const key = normalizeKey(status);
  if (key === 'open') return 'critical';
  if (key === 'investigating') return 'medium';
  if (key === 'resolved') return 'success';
  return 'neutral';
};

export const fimChangeTypeToTone = (changeType: string | undefined): EdrStatusTone => {
  const key = normalizeKey(changeType);
  if (key === 'deleted') return 'critical';
  if (key === 'modified') return 'medium';
  if (key === 'added' || key === 'created') return 'brand';
  return 'neutral';
};

export const formatFimRuleDetail = (rule: WazuhFimRule | undefined): string => {
  if (!rule) return '—';
  const description = rule.description?.trim();
  const id = rule.id != null && String(rule.id).trim() ? String(rule.id).trim() : '';
  const level = typeof rule.level === 'number' ? `L${rule.level}` : '';
  const suffixParts = [id ? `#${id}` : '', level].filter(Boolean);
  const suffix = suffixParts.length ? ` ${suffixParts.join(' - ')}` : '';
  if (description) return `${description}${suffix}`.trim();
  return suffix.trim() || '—';
};

export const formatFimFileName = (filePath: string | undefined): string =>
  filePath?.split(/[\\/]/).pop() || filePath || '—';

/** Id for GET /file-monitoring/events/{id} — prefers `id`, then `_id`. */
export const getFimEventId = (
  item: Pick<WazuhFimEventItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

export const normalizeFimKey = normalizeKey;

export const FIM_CHANGE_TYPE_FILTER_OPTIONS = [
  { label: 'Added', value: 'added' },
  { label: 'Modified', value: 'modified' },
  { label: 'Deleted', value: 'deleted' },
];


export const FIM_RULE_LEVEL_FILTER_OPTIONS = Array.from({ length: 15 }, (_, index) => {
  const level = String(index + 2);
  return { label: level, value: level };
});

const formatFimKpiNumber = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

export type FimAgentEventsFilters = {
  changeType?: string;
  severityBand?: string;
  ruleLevel?: string;
  user?: string;
};

export const buildFimAgentEventsQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: FimAgentEventsFilters = {}
): WazuhFimEventsQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.changeType ? { changeType: filters.changeType } : {}),
  ...(filters.severityBand ? { severityBand: filters.severityBand } : {}),
  ...(filters.ruleLevel ? { ruleLevel: filters.ruleLevel } : {}),
  ...(filters.user ? { user: filters.user } : {}),
});

export const buildFimUserFilterOptions = (
  items: WazuhFimEventItem[],
  selectedUser = ''
) => {
  const fromRows = items
    .map((item) => item.user?.trim())
    .filter((name): name is string => Boolean(name));
  const selected = selectedUser.split(',').map((name) => name.trim()).filter(Boolean);
  const names = Array.from(new Set([...fromRows, ...selected])).sort((a, b) =>
    a.localeCompare(b)
  );
  return names.map((name) => ({ label: name, value: name }));
};

export const FimSummaryKpiGrid: FC<{ summary: WazuhFimSummaryData | null }> = ({ summary }) => {
  const breakdown = summary?.changeTypeBreakdown;
  const changesTotal = breakdown
    ? (breakdown.added ?? 0) + (breakdown.modified ?? 0) + (breakdown.deleted ?? 0)
    : null;

  return (
    <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
      <EdrKpiCard
        label="File Changes"
        value={formatFimKpiNumber(changesTotal)}
        icon={<WarningIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.high}
        accent={EDR_COLORS.high}
      >
        <EdrStatusPill
          tone="brand"
          label={`${formatFimKpiNumber(breakdown?.added)} Added`}
          dot={false}
        />
        <EdrStatusPill
          tone="medium"
          label={`${formatFimKpiNumber(breakdown?.modified)} Modified`}
          dot={false}
        />
        <EdrStatusPill
          tone="critical"
          label={`${formatFimKpiNumber(breakdown?.deleted)} Deleted`}
          dot={false}
        />
      </EdrKpiCard>
      <EdrKpiCard
        label="File Changes Today"
        value={formatFimKpiNumber(summary?.fileChangesToday?.total)}
        icon={<AccessTimeIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.brand500}
      >
        Last 24 hours
      </EdrKpiCard>
      <EdrKpiCard
        label="Severity by Rules"
        value={formatFimKpiNumber(summary?.severityByRules?.total)}
        icon={<GavelIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.critical}
        accent={EDR_COLORS.high}
      >
        <EdrStatusPill
          tone="critical"
          label={`${formatFimKpiNumber(summary?.severityByRules?.critical)} Critical`}
          dot={false}
        />
        <EdrStatusPill
          tone="high"
          label={`${formatFimKpiNumber(summary?.severityByRules?.high)} High`}
          dot={false}
        />
        <EdrStatusPill
          tone="medium"
          label={`${formatFimKpiNumber(summary?.severityByRules?.medium)} Medium`}
          dot={false}
        />
      </EdrKpiCard>
      <EdrKpiCard
        label="Critical File Alerts"
        value={formatFimKpiNumber(summary?.criticalFileAlerts?.total)}
        icon={<ShieldIcon style={{ fontSize: 18 }} />}
        iconColor={EDR_COLORS.critical}
        accent={EDR_COLORS.critical}
      >
        Protected system files
      </EdrKpiCard>
    </Box>
  );
};

export const FimChangeTypeTag: FC<{ value: string | undefined }> = ({ value }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: EDR_COLORS.textBody,
    }}
  >
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: CHANGE_TYPE_COLORS[normalizeKey(value)] ?? EDR_COLORS.slate500,
      }}
    />
    {toFimTitle(value)}
  </span>
);

type FimListColumnScope = 'global' | 'endpoint';

export const getFimEventListColumns = (scope: FimListColumnScope): Column[] => {
  const isEndpoint = scope === 'endpoint';

  return [
    {
      id: 'filePath',
      label: 'File Path',
      width: isEndpoint ? '24%' : '18%',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhFimEventItem).filePath || '—'}
          style={{ ...edrMonoStyle, fontWeight: 500 }}
          maxWidth={isEndpoint ? 320 : 220}
        />
      ),
    },
    {
      id: 'changeType',
      label: 'Change Type',
      width: isEndpoint ? '12%' : '10%',
      render: (row) => (
        <FimChangeTypeTag value={(row as unknown as WazuhFimEventItem).changeType} />
      ),
    },
    ...(isEndpoint
      ? []
      : [
          {
            id: 'endpoint',
            label: 'Endpoint',
            width: '12%',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhFimEventItem).endpoint || '—'}
                style={edrMonoStyle}
                maxWidth={140}
              />
            ),
          },
          {
            id: 'hostName',
            label: 'Endpoint Label',
            width: '11%',
            render: (row) => (
              <EdrTruncatedText
                value={formatEndpointLabel((row as unknown as WazuhFimEventItem).hostName)}
                style={edrMutedStyle}
                maxWidth={140}
              />
            ),
          },
          {
            id: 'organization',
            label: 'Organization',
            width: '12%',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhFimEventItem).organization || '—'}
                style={edrMutedStyle}
                maxWidth={140}
              />
            ),
          },
        ]),
    {
      id: 'user',
      label: 'User',
      width: isEndpoint ? '10%' : '8%',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhFimEventItem).user || '—'}
          style={edrMonoStyle}
          maxWidth={isEndpoint ? 120 : 100}
        />
      ),
    },
    {
      id: 'severity',
      label: 'Severity',
      width: isEndpoint ? '12%' : '10%',
      render: (row) => {
        const item = row as unknown as WazuhFimEventItem;
        return (
          <EdrSeverityPill
            level={fimSeverityToLevel(item.severity)}
            label={toFimTitle(item.severity) || '—'}
          />
        );
      },
    },
    {
      id: 'ruleLevel',
      label: 'Rule Level',
      width: isEndpoint ? '10%' : '8%',
      render: (row) => {
        const level = (row as unknown as WazuhFimEventItem).rule?.level;
        return (
          <span style={edrMutedStyle}>
            {typeof level === 'number' ? String(level) : '—'}
          </span>
        );
      },
    },
    {
      id: 'ruleDetail',
      label: 'Rule Detail',
      width: isEndpoint ? '16%' : '12%',
      render: (row) => (
        <EdrTruncatedText
          value={formatFimRuleDetail((row as unknown as WazuhFimEventItem).rule)}
          style={edrMutedStyle}
          maxWidth={isEndpoint ? 240 : 180}
        />
      ),
    },
    {
      id: 'timestamp',
      label: 'Time',
      width: isEndpoint ? '16%' : '10%',
      render: (row) => (
        <EdrDateTime value={(row as unknown as WazuhFimEventItem).timestamp} />
      ),
    },
  ];
};

const formatFimEndpointCount = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const fimCountCell = (value: number | undefined | null, criticalTint = false) => {
  const count = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return (
    <span
      style={{
        fontWeight: 600,
        color: criticalTint && count > 0 ? EDR_COLORS.critical : EDR_COLORS.textPrimary,
      }}
    >
      {formatFimEndpointCount(count)}
    </span>
  );
};

export const getFimAffectedEndpointListColumns = (): Column[] => [
  {
    id: 'endpoint',
    label: 'Endpoint',
    render: (row) => {
      const item = row as unknown as WazuhFimEndpointItem;
      const canOpen = Boolean(item.systemWazuhAgentId?.trim());
      return (
        <span style={edrEndpointNameLinkStyle(canOpen)}>
          {item.endpoint ?? item.name ?? item.systemWazuhAgentId ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'hostName',
    label: 'Endpoint Label',
    render: (row) => (
      <span style={edrMutedStyle}>
        {formatEndpointLabel((row as unknown as WazuhFimEndpointItem).hostName)}
      </span>
    ),
  },
  {
    id: 'monitoredFiles',
    label: 'Monitored Files',
    align: 'center',
    render: (row) => (
      <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {formatFimEndpointCount((row as unknown as WazuhFimEndpointItem).monitoredFiles?.total)}
      </span>
    ),
  },
  {
    id: 'fileChangesToday',
    label: 'Changes Today',
    align: 'center',
    render: (row) => (
      <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {formatFimEndpointCount((row as unknown as WazuhFimEndpointItem).fileChangesToday?.total)}
      </span>
    ),
  },
  {
    id: 'added',
    label: 'Added',
    align: 'center',
    render: (row) =>
      fimCountCell((row as unknown as WazuhFimEndpointItem).changeTypeBreakdown?.added),
  },
  {
    id: 'modified',
    label: 'Modified',
    align: 'center',
    render: (row) =>
      fimCountCell((row as unknown as WazuhFimEndpointItem).changeTypeBreakdown?.modified),
  },
  {
    id: 'deleted',
    label: 'Deleted',
    align: 'center',
    render: (row) =>
      fimCountCell((row as unknown as WazuhFimEndpointItem).changeTypeBreakdown?.deleted),
  },
  {
    id: 'criticalFileAlerts',
    label: 'Critical Alerts',
    align: 'center',
    render: (row) =>
      fimCountCell((row as unknown as WazuhFimEndpointItem).criticalFileAlerts?.total, true),
  },
  {
    id: 'critical',
    label: 'Critical',
    align: 'center',
    render: (row) =>
      fimCountCell((row as unknown as WazuhFimEndpointItem).severityByRules?.critical, true),
  },
  {
    id: 'high',
    label: 'High',
    align: 'center',
    render: (row) => fimCountCell((row as unknown as WazuhFimEndpointItem).severityByRules?.high),
  },
  {
    id: 'medium',
    label: 'Medium',
    align: 'center',
    render: (row) => fimCountCell((row as unknown as WazuhFimEndpointItem).severityByRules?.medium),
  },
];

export const buildFimDetailProps = (
  item: WazuhFimEventItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = fimSeverityToLevel(item.severity);
  const changeLabel = toFimTitle(item.changeType) || 'Change';
  const statusLabel = toFimTitle(item.status) || changeLabel;
  const statusTone = item.status
    ? fimStatusToTone(item.status)
    : fimChangeTypeToTone(item.changeType);
  const fileName = formatFimFileName(item.filePath);

  return {
    backLink: {
      label: 'Back to File Monitoring',
      to: getSecurityNavItemPath('file-integrity-monitoring') ?? '',
    },
    title: fileName,
    badges: (
      <>
        <EdrSeverityPill level={severityLevel} label={toFimTitle(item.severity) || '—'} />
        <EdrStatusPill tone={fimChangeTypeToTone(item.changeType)} label={changeLabel} />
      </>
    ),
    metaLine: (
      <>
        {changeLabel}
        {' · '}
        {formatEdrDateTime(item.timestamp)}
        {' on '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint || '—'}</strong>
      </>
    ),
    main: (
      <>
        <EdrDetailSectionCard icon={<FolderIcon style={{ fontSize: 16 }} />} title="File Event">
          <EdrDetailKeyValueRows
            rows={[
              { label: 'File Name', value: fileName },
              { label: 'Full Path', value: item.filePath || '—', mono: true },
              { label: 'Change Type', value: changeLabel },
              { label: 'User', value: item.user || '—', mono: true },
              { label: 'Timestamp', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Rule">
          {item.rule?.description ? (
            <Box mb={1.5}>
              <EdrExpandableText text={item.rule.description} lines={3} />
            </Box>
          ) : null}
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Level',
                value: typeof item.rule?.level === 'number' ? String(item.rule.level) : '—',
              },
              {
                label: 'Rule ID',
                value: item.rule?.id != null ? String(item.rule.id) : '—',
                mono: true,
              },
              {
                label: 'Groups',
                value: item.rule?.groups?.length ? item.rule.groups.join(', ') : '—',
              },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <>
        <EdrDetailEndpointSidebar
          endpoint={item.endpoint || '—'}
          hostName={item.hostName}
          statusLabel={statusLabel}
          statusTone={statusTone}
          organization={item.organization}
        />
        <EdrDetailSectionCard
          icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
          title="Event Details"
        >
          <EdrDetailKeyValueRows
            rows={[
              { label: 'Severity', value: toFimTitle(item.severity) || '—' },
              { label: 'Status', value: toFimTitle(item.status) || '—' },
              { label: 'Wazuh Agent', value: item.wazuhAgentId || '—', mono: true },
              { label: 'Wazuh Alert', value: item.wazuhAlertId || '—', mono: true },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
  };
};
